import React, { useState, useEffect, useGlobal } from "reactn";
import { toast } from "react-toastify";
import axios from "axios";
import { NewTabbedTable } from "../../../components/UI";
import Dropdown from "rc-dropdown";
import Menu, { Item as MenuItem } from "rc-menu";
import DisputeEvidenceModal from "../../../components/modals/DisputeEvidenceModal";

const calculateDisputeAmounts = disputes => {
  const sumAmounts = category =>
    disputes[category].reduce((sum, dispute) => sum + dispute.amount / 100, 0);

  return {
    pending: sumAmounts("Needs Response") + sumAmounts("Under Review"),
    won: sumAmounts("Won"),
    lost: sumAmounts("Lost"),
    fees: disputes["All"].length * 15
  };
};

const Disputes = ({ history }) => {
  const [disputes, setDisputes] = useState({
    All: [],
    "Needs Response": [],
    "Under Review": [],
    Lost: [],
    Won: []
  });
  const [selectedDispute, setSelectedDispute] = useState(null);
  const [openEvidenceModal, setOpenEvidenceModal] = useState(false);
  const [allowed, setAllowed] = useState(false);
  const [global, setGlobal] = useGlobal();

  useEffect(() => {
    setGlobal({
      partnerPathway: [
        ...global.partnerPathway.slice(0, 1),
        { label: "Disputes", to: "/disputes" }
      ]
    });
    fetchDisputes();
  }, []);

  const fetchDisputes = async () => {
    try {
      const perm = await axios.get(`${process.env.REACT_APP_API}/partner/permissions/disputes`);
      if (!global.dsUser.sub_user && perm.data.success && perm.data.permission) {
        setAllowed(perm.data.permission.has_access);
      }
      const res = await axios.get(
        `${process.env.REACT_APP_API}/partners/stripe/disputes`
      );
      if (res.data.success) {
        console.log("Disputes fetched successfully:", res.data.disputes);
        const disputes = res.data.disputes;
        setDisputes(disputes);
      } else {
        toast.error("Failed to fetch disputes.");
      }
    } catch (error) {
      console.error("Error fetching disputes:", error);
      toast.error("An error occurred while fetching disputes.");
    }
  };

  const handleMenuClick = (dispute, event) => {
    if (event.key === "viewOrder") {
        history.push(`orders/orders-receipt/${dispute.metadata.orderId}`);
    } else if (event.key === "uploadEvidence") {
      setSelectedDispute(dispute);
      setOpenEvidenceModal(true);
    }
  };

  const menu = dispute => (
    <Menu onClick={event => handleMenuClick(dispute, event)}>
      <MenuItem key="viewOrder">View Order</MenuItem>
      {(!global.dsUser.sub_user || global.dsUser.accesspoints.Financials["Disputes"].edit) ?
      <MenuItem key="uploadEvidence">Upload Evidence</MenuItem>
      : null}
    </Menu>
  );

  const tabs = [
    "All",
    "Needs Response",
    "Under Review",
    "Lost",
    "Won"
  ];

  const getHeadings = () => {
    const headingsMap = {};
    tabs.forEach(tab => {
      headingsMap[tab] = headings;
    });
    return headingsMap;
  };

  const headings = [
    {
      id: "date",
      label: "Date",
      customCell: d => (
        <td className="table-custom-text">{new Date(d.created * 1000).toLocaleDateString("en-US", { month: "long", day: "2-digit", year: "numeric" })}</td>
      )
    },
    {
      id: "item",
      label: "Item",
      customCell: d => <td className="table-custom-text">{d.metadata.item || "unknown"}</td>
    },
    {
        id: "purchaser",
        label: "Purchaser",
        customCell: d => <td className="table-custom-text">{d.metadata.purchaser || "unknown"}</td>
    },
    {
      id: "amount",
      label: "Amount",
      customCell: d => <td className="table-custom-text">${(d.amount / 100).toFixed(2)}</td>
    },
    {
      id: "status",
      label: "Status",
      customCell: d => {
        let color = "";
        let text = "";
        switch (d.status) {
          case "warning_needs_response":
            color = "#eba93e";
            text = "Needs Response";
            break;
          case "warning_under_review":
            color = "#eba93e";
            text = "Under Review";
            break;
          case "warning_closed":
            color = "#ff3b8e";
            text = "Closed";
            break;
          case "needs_response":
            color = "#ff3b8e";
            text = "Needs Response";
            break;
          case "under_review":
            color = "rgba(92, 200, 220)";
            text = "Under Review";
            break;
          case "won":
            color = "#1bc88a";
            text = "Won";
            break;
          case "lost":
            color = "#ff3b8e";
            text = "Lost";
            break;
          default:
            color = "#d3d3d3";
            text = "Unknown";
            break;
        }
        return (
          <td className="table-custom-text">
            <div
              style={{
                textAlign: "right",
                display: "flex"
              }}
            >
              <span
                style={{
                  minWidth: "100px",
                  background: color,
                  padding: "3px",
                  borderRadius: "13px",
                  color: "white",
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  fontFamily: "Open Sans",
                  fontSize: "12px",
                  fontWeight: 600
                }}
              >
                {text}
              </span>
            </div>
          </td>
        );
      }
    },
    {
        id: "reason",
        label: "Reason",
        customCell: d => <td className="table-custom-text">{d.reason || "unknown"}</td>
    },
    {
        id: "due by",
        label: "Evidence Due By",
        customCell: d => {
          const dueDate = new Date(d.evidence_details.due_by * 1000).toLocaleDateString("en-US", { month: "long", day: "2-digit", year: "numeric" });
          return <td className="table-custom-text">{dueDate}</td>;
        }
    },
    {
      id: "actions",
      label: "Actions",
      customCell: d => (
        <td className="table-custom-text">
          <Dropdown trigger={["click"]} overlay={menu(d)} animation="slide-up">
            <button className="table-action-button">Action</button>
          </Dropdown>
        </td>
      )
    }
  ];

  const { pending, won, lost, fees } = calculateDisputeAmounts(disputes);

  if (!allowed) {
    return (
      <div className="container-fluid">
        <div className="cont">
          <div className="row">
            <h1 data-testid="heading-no-permission">Disputes</h1>
          </div>
          <div className="row">
            <p>
              You do not have permission to view this page. Contact admin.{" "}
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      {openEvidenceModal && (
        <DisputeEvidenceModal
          dispute={selectedDispute}
          onClose={() => {
            setOpenEvidenceModal(false);
            fetchDisputes();}}
        />
      )}
      <div className="container-fluid">
        <div className="row cont">
          <div className="col-md-6">
            <h1>Disputes</h1>
          </div>
        </div>
        <div className="cont">
            <div className="row">
              <div className="col-lg-3">
                <div className="money-box mt-0">
                  <div className="row">
                    <div className="col-lg-12 col-sm-6">
                      <div className="money-box-inner">
                        <h2>AMOUNT PENDING</h2>
                        <h3>
                          {pending.toLocaleString(
                            "en-US",
                            {
                              style: "currency",
                              currency: "USD"
                            }
                          )}
                        </h3>
                      </div>
                    </div>
                    <div className="col-lg-12 col-sm-6">
                      <div className="money-box-inner">
                        <h2>AMOUNT WON</h2>
                        <h3>
                          {won.toLocaleString(
                            "en-US",
                            {
                              style: "currency",
                              currency: "USD"
                            }
                          )}
                        </h3>
                      </div>
                    </div>
                    <div className="col-lg-12 col-sm-6">
                      <div className="money-box-inner">
                        <h2>AMOUNT LOST</h2>
                        <h3>
                          {lost.toLocaleString(
                            "en-US",
                            {
                              style: "currency",
                              currency: "USD"
                            }
                          )}
                        </h3>
                      </div>
                    </div>
                    <div className="col-lg-12 col-sm-6">
                      <div className="money-box-inner">
                        <h2>FEES INCURRED</h2>
                        <h3
                        style={{ paddingBottom: "0px", paddingTop: "20px" }}
                        >
                          {fees.toLocaleString(
                            "en-US",
                            {
                              style: "currency",
                              currency: "USD"
                            }
                          )}
                        </h3>
                        <p
                          style={{
                            color: "#a1a1a1",
                            textAlign: "center",
                            paddingBottom: "20px",
                            fontStyle: "italic",
                            fontSize: "0.9rem"
                          }}
                        >
                            Stripe charges a $15 fee for each dispute.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="col-lg-9">
                <NewTabbedTable
                  data={disputes}
                  tabs={tabs}
                  headings={getHeadings()}
                />
              </div>
            </div>
          </div>
      </div>
    </>
  );
};

export default Disputes;
